import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, interval, Subscription, throwError } from 'rxjs';
import { switchMap, takeWhile, catchError, timeout, retry, map } from 'rxjs/operators';
import { VmStatusModel, VmCreationStatus } from '../models/vm-status.model';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class VmStatusPollingService {
  private readonly apiUrl = environment.apiEndpoint;
  private readonly pollingInterval = 30000; // 30 seconds (faster for active VM creation)
  private readonly slowPollingInterval = 120000; // 2 minutes (for long-running operations)
  private readonly maxRetries = 3;
  private readonly retryDelay = 5000; // 5 seconds
  private readonly requestTimeout = 30000; // 30 seconds

  private pollingIntervals = new Map<string, Subscription>();
  private activePollingStatus = new Map<string, VmCreationStatus>();
  private pollCount = new Map<string, number>(); // Track poll count per ticket

  constructor(private http: HttpClient) {}

  /**
   * Get VM status for a specific ticket
   * @param ticketId The ticket ID
   * @returns Observable with VM status data
   */
  getVmStatus(ticketId: string): Observable<VmStatusModel> {
    const url = `${this.apiUrl}vdi-approval/status/${ticketId}`;
    console.log(`🔍 [VM Status Polling] Getting VM status for ticket ${ticketId}`);
    console.log(`🌐 [VM Status Polling] Request URL: ${url}`);

    return this.http.get<VmStatusModel>(url).pipe(
      timeout(this.requestTimeout),
      retry(this.maxRetries),
      map(response => {
        console.log(`✅ [VM Status Polling] Got VM status response:`, response);
        console.log(`📊 [VM Status Polling] VM Creation Status: ${response?.vmCreationStatus}`);
        console.log(`🖥️ [VM Status Polling] VM Name: ${response?.vmName || 'N/A'}`);
        console.log(`🌐 [VM Status Polling] VM IP: ${response?.publicIpAddress || 'N/A'}`);
        return response;
      }),
      catchError((error) => {
        // 404 is expected for non-VDI tickets - log as info, not error
        if (error instanceof HttpErrorResponse && error.status === 404) {
          console.log(`ℹ️ [VM Status Polling] No VDI approval record for ticket ${ticketId} (expected for non-VDI tickets)`);
          return this.handleHttpError(error);
        }

        // Log other errors normally
        console.error(`❌ [VM Status Polling] Error fetching VM status for ticket ${ticketId}:`, error);
        console.error(`📊 [VM Status Polling] Error status: ${error.status}`);
        console.error(`📝 [VM Status Polling] Error message: ${error.message}`);

        // Transform HTTP errors to more user-friendly messages
        if (error instanceof HttpErrorResponse) {
          return this.handleHttpError(error);
        }

        return throwError(() => new Error('Failed to fetch VM status. Please try again later.'));
      })
    );
  }

  /**
   * Get status history for a specific ticket
   * @param ticketId The ticket ID
   * @returns Observable with status history entries
   */
  getStatusHistory(ticketId: string): Observable<any[]> {
    const url = `${this.apiUrl}vdi-approval/status/${ticketId}/history`;

    return this.http.get<any[]>(url).pipe(
      timeout(this.requestTimeout),
      retry(1),
      catchError((error) => {
        console.error(`Error fetching status history for ticket ${ticketId}:`, error);

        if (error instanceof HttpErrorResponse) {
          return this.handleHttpError(error);
        }

        return throwError(() => new Error('Failed to fetch status history. Please try again later.'));
      })
    );
  }

  /**
   * Retry VM creation for a failed ticket
   * @param ticketId The ticket ID
   * @returns Observable with retry response
   */
  retryVmCreation(ticketId: string): Observable<any> {
    const url = `${this.apiUrl}vdi-approval/status/${ticketId}/retry`;

    return this.http.post<any>(url, {}).pipe(
      timeout(this.requestTimeout),
      retry(1),
      catchError((error) => {
        console.error(`Error retrying VM creation for ticket ${ticketId}:`, error);

        if (error instanceof HttpErrorResponse) {
          return this.handleHttpError(error);
        }

        return throwError(() => new Error('Failed to retry VM creation. Please contact administrator.'));
      })
    );
  }

  /**
   * Start polling VM status for a ticket
   * @param ticketId The ticket ID
   * @param callback Callback function to handle status updates
   */
  startPolling(ticketId: string, callback: (status: VmStatusModel) => void): void {
    // Stop any existing polling for this ticket
    this.stopPolling(ticketId);

    // Make initial call immediately
    this.getVmStatus(ticketId).subscribe({
      next: (status) => {
        this.activePollingStatus.set(ticketId, status.vmCreationStatus);
        callback(status);

        // Start polling only if status is active
        if (this.shouldContinuePolling(status.vmCreationStatus)) {
          this.startPollingInterval(ticketId, callback);
        }
      },
      error: (error) => {
        // 404 is expected for non-VDI tickets - just log info
        if (error.message?.includes('not found') || error.message?.includes('not exist')) {
          console.log(`ℹ️ [VM Status Polling] Initial poll: No VDI approval for ticket ${ticketId} (not a VDI ticket)`);
        } else {
          console.error(`❌ [VM Status Polling] Initial polling error for ticket ${ticketId}:`, error);
        }
        // Don't start polling if initial call fails
      }
    });
  }

  /**
   * Stop polling VM status for a ticket
   * @param ticketId The ticket ID
   */
  stopPolling(ticketId: string): void {
    const subscription = this.pollingIntervals.get(ticketId);
    if (subscription) {
      subscription.unsubscribe();
      this.pollingIntervals.delete(ticketId);
      this.activePollingStatus.delete(ticketId);
    }
  }

  /**
   * Check if polling should continue for a given status
   * @param status Current VM creation status
   * @returns Whether polling should continue
   */
  private shouldContinuePolling(status: VmCreationStatus): boolean {
    return status === VmCreationStatus.NotStarted ||
           status === VmCreationStatus.InProgress;
  }

  /**
   * Start the polling interval for a ticket
   * @param ticketId The ticket ID
   * @param callback Callback function to handle status updates
   */
  private startPollingInterval(ticketId: string, callback: (status: VmStatusModel) => void): void {
    // Initialize poll count
    this.pollCount.set(ticketId, 0);

    // Get dynamic polling interval based on poll count
    const getDynamicInterval = (): number => {
      const count = this.pollCount.get(ticketId) || 0;
      // First 10 polls: every 30 seconds (5 minutes total)
      // After that: every 2 minutes (for longer operations)
      return count < 10 ? this.pollingInterval : this.slowPollingInterval;
    };

    // Create a recursive polling function with dynamic intervals
    const createPolling = (): Subscription => {
      const currentInterval = getDynamicInterval();
      console.log(`🔄 [Polling Service] Starting interval: ${currentInterval / 1000}s for ticket ${ticketId} (poll count: ${this.pollCount.get(ticketId)})`);

      return interval(currentInterval)
        .pipe(
          switchMap(() => {
            const count = this.pollCount.get(ticketId) || 0;
            this.pollCount.set(ticketId, count + 1);
            console.log(`📊 [Polling Service] Poll #${count + 1} for ticket ${ticketId}`);
            return this.getVmStatus(ticketId);
          }),
          takeWhile(() => {
            const currentStatus = this.activePollingStatus.get(ticketId);
            return currentStatus ? this.shouldContinuePolling(currentStatus) : false;
          }),
          catchError((error) => {
            // 404 means ticket is not a VDI ticket - stop polling quietly
            if (error.message?.includes('not found') || error.message?.includes('not exist')) {
              console.log(`ℹ️ [VM Status Polling] Stopping polling for ticket ${ticketId} (not a VDI ticket)`);
              this.stopPolling(ticketId);
              return throwError(() => error);
            }

            console.error(`❌ [VM Status Polling] Polling error for ticket ${ticketId}:`, error);

            // Try one more time before stopping
            return this.getVmStatus(ticketId).pipe(
              catchError(() => {
                console.error(`❌ [VM Status Polling] Final polling attempt failed for ticket ${ticketId}:`, error);
                this.stopPolling(ticketId);
                return throwError(() => error);
              })
            );
          })
        )
        .subscribe({
          next: (status) => {
            this.activePollingStatus.set(ticketId, status.vmCreationStatus);
            callback(status);

            // If we've switched intervals, restart with new interval
            const shouldSwitchInterval = (this.pollCount.get(ticketId) || 0) === 10;
            if (shouldSwitchInterval && this.shouldContinuePolling(status.vmCreationStatus)) {
              console.log(`🔄 [Polling Service] Switching to slower interval (${this.slowPollingInterval / 1000}s) for ticket ${ticketId}`);
              this.stopPolling(ticketId);
              const newSubscription = createPolling();
              this.pollingIntervals.set(ticketId, newSubscription);
            }
          },
          error: (error) => {
            // 404 is expected for non-VDI tickets - just stop polling quietly
            if (error.message?.includes('not found') || error.message?.includes('not exist')) {
              console.log(`ℹ️ [VM Status Polling] Stopped polling ticket ${ticketId} (not a VDI ticket)`);
            } else {
              console.error(`❌ [VM Status Polling] Critical polling error for ticket ${ticketId}:`, error);
            }
            this.stopPolling(ticketId);
          }
        });
    };

    const subscription = createPolling();
    this.pollingIntervals.set(ticketId, subscription);
  }

  /**
   * Handle HTTP errors and transform them to user-friendly messages
   * @param error HTTP error response
   * @returns Observable with error message
   */
  private handleHttpError(error: HttpErrorResponse): Observable<never> {
    let message = 'An error occurred while fetching VM status.';

    switch (error.status) {
      case 400:
        message = 'Invalid request. Please check the ticket ID and try again.';
        break;
      case 401:
        message = 'You are not authorized to view this ticket. Please log in.';
        break;
      case 403:
        message = 'You do not have permission to view this ticket. Please contact your administrator.';
        break;
      case 404:
        message = 'VM status not found for this ticket. The ticket may not exist or may not be a VDI request.';
        break;
      case 500:
        message = 'Server error occurred. Please try again later or contact support.';
        break;
      case 0:
        message = 'Network error. Please check your internet connection and try again.';
        break;
      default:
        message = `Error ${error.status}: ${error.statusText || 'Unknown error occurred'}`;
        break;
    }

    return throwError(() => new Error(message));
  }

  /**
   * Get current polling status for all active tickets
   * @returns Map of ticket IDs to their current polling status
   */
  getActivePollingStatus(): Map<string, VmCreationStatus> {
    return new Map(this.activePollingStatus);
  }

  /**
   * Check if a specific ticket is currently being polled
   * @param ticketId The ticket ID
   * @returns Whether the ticket is being polled
   */
  isPolling(ticketId: string): boolean {
    return this.pollingIntervals.has(ticketId);
  }

  /**
   * Get the number of currently active polling subscriptions
   * @returns Number of active polls
   */
  getActivePollCount(): number {
    return this.pollingIntervals.size;
  }

  /**
   * Clean up all polling intervals when service is destroyed
   */
  ngOnDestroy(): void {
    this.pollingIntervals.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.pollingIntervals.clear();
    this.activePollingStatus.clear();
  }
}